import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Tag, Plus, Trash2, Percent, Coins } from "lucide-react";

const rupiah = (v) => "Rp" + (Math.round(v || 0)).toLocaleString("id-ID");

export default function Promos() {
  const [rows, setRows] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", type: "percent", value: 0, min_purchase: 0, outlet_id: "", active: true });

  const load = () => api.get("/promos").then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/outlets").then(({ data }) => setOutlets(data)); }, []);

  async function save() {
    if (!form.name) return toast.error("Nama promo wajib");
    if (form.value <= 0) return toast.error("Nilai harus > 0");
    try {
      await api.post("/promos", { ...form, outlet_id: form.outlet_id || null });
      toast.success("Promo tersimpan");
      setOpen(false);
      setForm({ name: "", code: "", type: "percent", value: 0, min_purchase: 0, outlet_id: "", active: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }

  async function toggleActive(p) {
    await api.put(`/promos/${p.id}`, { active: !p.active });
    load();
  }

  async function del(p) {
    if (!window.confirm(`Hapus promo "${p.name}"?`)) return;
    await api.delete(`/promos/${p.id}`);
    load();
  }

  return (
    <div className="page-shell">
      <div className="flex items-center justify-between">
        <div data-workspace-redundant-header>
          <h1 className="text-2xl font-extrabold font-display flex items-center gap-2"><Tag className="w-6 h-6 text-orange-500"/>Promo & Diskon</h1>
          <p className="text-sm text-slate-500">Diskon persentase atau nominal, otomatis muncul di POS</p>
        </div>
        <Button data-testid="promo-create-btn" onClick={()=>setOpen(true)} className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Promo Baru</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {rows.map(p => (
          <Card key={p.id} data-testid={`promo-card-${p.id}`} className="p-4 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-bold text-lg">{p.name}</div>
                {p.code && <div className="text-xs font-mono text-slate-500">{p.code}</div>}
              </div>
              <div className="flex items-center gap-2">
                {p.type === "percent" ? <Percent className="w-4 h-4 text-orange-500"/> : <Coins className="w-4 h-4 text-orange-500"/>}
                <span className="font-extrabold text-orange-600 num">
                  {p.type === "percent" ? `${p.value}%` : rupiah(p.value)}
                </span>
              </div>
            </div>
            <div className="text-xs text-slate-500 space-y-0.5">
              {p.min_purchase > 0 && <div>Min beli: <b className="num">{rupiah(p.min_purchase)}</b></div>}
              <div>Outlet: <b>{outlets.find(o => o.id === p.outlet_id)?.name || "Semua outlet"}</b></div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Badge variant={p.active ? "default" : "outline"} className={p.active ? "bg-emerald-500" : ""}>{p.active ? "AKTIF" : "NONAKTIF"}</Badge>
              <Button variant="outline" size="sm" onClick={()=>toggleActive(p)} className="ml-auto">{p.active ? "Matikan" : "Aktifkan"}</Button>
              <Button variant="outline" size="sm" onClick={()=>del(p)} className="text-rose-600"><Trash2 className="w-3.5 h-3.5"/></Button>
            </div>
          </Card>
        ))}
        {!rows.length && <div className="col-span-full text-center text-slate-400 py-16">Belum ada promo. Buat promo pertama Anda.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Promo Baru</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs font-medium">Nama Promo</label>
              <Input data-testid="promo-name-input" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Diskon Awal Bulan"/></div>
            <div><label className="text-xs font-medium">Kode (opsional)</label>
              <Input value={form.code} onChange={e=>setForm({...form,code:e.target.value})} placeholder="HEMAT10"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium">Jenis</label>
                <Select value={form.type} onValueChange={v=>setForm({...form,type:v})}>
                  <SelectTrigger data-testid="promo-type-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Persentase (%)</SelectItem>
                    <SelectItem value="nominal">Nominal (Rp)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><label className="text-xs font-medium">Nilai</label>
                <Input data-testid="promo-value-input" type="number" value={form.value} onChange={e=>setForm({...form,value:Number(e.target.value)})}/></div>
            </div>
            <div><label className="text-xs font-medium">Min. Pembelian (opsional)</label>
              <Input type="number" value={form.min_purchase} onChange={e=>setForm({...form,min_purchase:Number(e.target.value)})}/></div>
            <div><label className="text-xs font-medium">Outlet</label>
              <Select value={form.outlet_id || "all"} onValueChange={v=>setForm({...form,outlet_id: v==="all" ? "" : v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua outlet</SelectItem>
                  {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.brand} · {o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button data-testid="promo-save-btn" onClick={save} className="bg-orange-500 hover:bg-orange-600">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
